import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import type { WorkflowNode } from '@/pages/crm/shared/types';
import { workflowGraphSnapshotSchema } from '@easyflow/shared';

export const TRIGGER_LABELS: Record<string, string> = {
  trigger_manual: 'Manuale',
  trigger_scheduled: 'Schedulato',
  trigger_webhook: 'Webhook',
  trigger_whatsapp_received: 'WhatsApp ricevuto',
  trigger_sms_received: 'SMS ricevuto',
  trigger_call_answered: 'Chiamata risposta',
  trigger_call_hangup: 'Chiamata terminata',
  trigger_voicemail_left: 'Voicemail ricevuta',
  trigger_voicemail_received: 'Voicemail ricevuta',
  trigger_lead_created: 'Lead creato',
  trigger_lead_stage_changed: 'Lead cambio stato',
  trigger_customer_created: 'Cliente creato',
  trigger_form_submitted: 'Form inviato',
  trigger_tag_added: 'Tag aggiunto',
  trigger_message_delivered: 'Messaggio consegnato',
  trigger_message_failed: 'Messaggio fallito',
  trigger_message_read: 'Messaggio letto',
  trigger_campaign_event: 'Evento campagna',
  trigger_appointment_created: 'Appuntamento creato',
  trigger_appointment_reminder: 'Promemoria appuntamento',
  trigger_task_created: 'Task creato',
  trigger_task_status_changed: 'Task cambio stato',
  trigger_task_assigned: 'Task assegnato',
  trigger_task_completed: 'Task completato',
  trigger_stock_below_threshold: 'Scorta sotto soglia',
  trigger_lot_expiring: 'Lotto in scadenza',
  trigger_stock_movement_created: 'Movimento magazzino',
  trigger_hr_request_created: 'Richiesta HR creata',
  trigger_hr_request_status_changed: 'Richiesta HR cambio stato',
  trigger_hr_clock_in: 'Timbratura entrata',
  trigger_hr_clock_out: 'Timbratura uscita',
  trigger_hr_skill_assigned: 'Skill HR assegnata',
  // Legacy/raw aliases
  whatsapp_received: 'WhatsApp ricevuto',
  sms_received: 'SMS ricevuto',
  call_ended: 'Chiamata terminata',
  call_answered: 'Chiamata risposta',
  voicemail_received: 'Voicemail ricevuta',
  lead_created: 'Lead creato',
  lead_stage_changed: 'Lead cambio stato',
  customer_created: 'Cliente creato',
  form_submitted: 'Form inviato',
  tag_added: 'Tag aggiunto',
  webhook: 'Webhook',
};

export function formatTriggerLabel(triggerType: string | undefined | null): string {
  if (!triggerType) return 'Sconosciuto';
  if (TRIGGER_LABELS[triggerType]) return TRIGGER_LABELS[triggerType];
  const cleaned = triggerType.replace(/^trigger_/, '').replace(/_/g, ' ');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

export function triggerBadgeClass(triggerType: string): string {
  if (triggerType.startsWith('trigger_whatsapp') || triggerType.includes('whatsapp')) {
    return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30';
  }
  if (triggerType.includes('sms') || triggerType.includes('message')) {
    return 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30';
  }
  if (triggerType.includes('call') || triggerType.includes('voicemail')) {
    return 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30';
  }
  if (triggerType.includes('lead') || triggerType.includes('customer') || triggerType.includes('form') || triggerType.includes('tag')) {
    return 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30';
  }
  if (triggerType.includes('schedul')) {
    return 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30';
  }
  if (triggerType.includes('webhook')) {
    return 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30';
  }
  if (triggerType.includes('manual')) {
    return 'bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30';
  }
  return 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/30';
}

/** Hex accent color associated to a trigger family (for gradient accent bars). */
export function triggerAccentColor(triggerType: string): string {
  if (triggerType.startsWith('trigger_whatsapp') || triggerType.includes('whatsapp')) return '#10b981';
  if (triggerType.includes('sms') || triggerType.includes('message')) return '#3b82f6';
  if (triggerType.includes('call') || triggerType.includes('voicemail')) return '#6366f1';
  if (triggerType.includes('lead') || triggerType.includes('customer') || triggerType.includes('form') || triggerType.includes('tag')) return '#f97316';
  if (triggerType.includes('schedul')) return '#f59e0b';
  if (triggerType.includes('webhook')) return '#8b5cf6';
  if (triggerType.includes('manual')) return '#64748b';
  return '#06b6d4';
}

export function formatRelative(dateStr: string | Date | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  const now = Date.now();
  const diff = now - d.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'Ora';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min fa`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h fa`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days} ${days === 1 ? 'giorno' : 'giorni'} fa`;
  if (days < 30) return `${Math.floor(days / 7)} settimane fa`;
  if (days < 365) return `${Math.floor(days / 30)} mesi fa`;
  return `${Math.floor(days / 365)} anni fa`;
}

export function formatAbsolute(dateStr: string | Date | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function fmtDuration(ms?: number | null): string | null {
  if (ms == null) return null;
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/** Returns the non-trigger nodes (the actual actions) of a workflow. */
export function getActionNodes(nodes: unknown): WorkflowNode[] {
  const parsed = workflowGraphSnapshotSchema.shape.nodes.safeParse(nodes);
  if (!parsed.success) return [];
  return parsed.data.filter((n) => n.type !== 'trigger' && n.type !== 'start');
}

/** True if any action node interacts with tasks / ToDo. */
export function interactsWithTasks(nodes: unknown): boolean {
  return getActionNodes(nodes).some((n) => {
    const type = (n.type || '').toLowerCase();
    const label = (typeof n.data?.label === 'string' ? n.data.label : '').toLowerCase();
    return type.includes('task') || type.includes('todo') || label.includes('task') || label.includes('to-do') || label.includes('todo');
  });
}

/** Human-readable label of a single node (action). */
export function nodeLabel(node: WorkflowNode): string {
  if (typeof node.data?.label === 'string' && node.data.label.trim()) return node.data.label;
  if (node.type) return formatTriggerLabel(node.type);
  return 'Azione';
}

/** Auto-generated one-line summary from a workflow's nodes. */
export function buildNodesSummary(triggerType: string, nodes: unknown): string {
  const actions = getActionNodes(nodes);
  const trigger = formatTriggerLabel(triggerType);
  if (actions.length === 0) return trigger;
  const parts = actions.slice(0, 4).map(nodeLabel);
  const suffix = actions.length > 4 ? `; +${actions.length - 4}` : '';
  return `Trigger: ${trigger}. Nodi presenti (ordine non garantito): ${parts.join('; ')}${suffix}`;
}

export function runStatusBadge(status: string) {
  if (status === 'pending') return <Badge variant="outline">In attesa</Badge>;
  if (status === 'cancel_requested') return <Badge variant="outline">Annullamento richiesto</Badge>;
  if (status === 'cancelled') return <Badge variant="outline">Annullamento confermato</Badge>;
  if (status === 'running')
    return (
      <Badge className="bg-blue-500/10 text-blue-600 dark:text-blue-300 border-blue-500/20 text-xs gap-1">
        <Loader2 className="w-3 h-3 animate-spin" />In esecuzione
      </Badge>
    );
  if (status === 'completed')
    return (
      <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 border-emerald-500/20 text-xs gap-1">
        <CheckCircle2 className="w-3 h-3" />Completato
      </Badge>
    );
  if (status === 'failed')
    return (
      <Badge className="bg-red-500/10 text-red-600 dark:text-red-300 border-red-500/20 text-xs gap-1">
        <XCircle className="w-3 h-3" />Fallito
      </Badge>
    );
  return <Badge className="bg-slate-500/10 text-slate-500 text-xs">{status}</Badge>;
}
